/**
 * Splitting a cell's text around what the user searched for.
 *
 * `components.md`: *"Substring match on GTIN with the matched span marked —
 * nobody types a 14-digit number from the start."*
 *
 * The second half of that sentence is the requirement. A brand looking for
 * `09520123456788` types the four digits they can read off the packet, and a
 * search that only matched from the start would find nothing while the row sat
 * on screen. `apps/api`'s `searchPredicate` answers the query side with a
 * `%…%` `LIKE`; this answers the rendering side, because a substring hit in the
 * middle of a fourteen-digit number is invisible without the mark.
 *
 * Pure and separate from the component for the usual reason: which spans matched
 * is arithmetic over two strings, and arithmetic should not need a DOM to check.
 */
/**
 * Split `text` into matched and unmatched runs.
 *
 * Case-insensitive, because the catalogue is typed by people and searched by
 * people; **every** occurrence is marked, not just the first, because a GTIN can
 * repeat a digit run and marking one of two would read as the wrong one being
 * found. Overlaps cannot arise — the scan advances past each match.
 *
 * An empty or whitespace-only query marks nothing and returns the text whole, so
 * a screen can call this unconditionally instead of branching on whether a
 * search is active.
 */
export function markMatch(text, query) {
    const needle = query.trim();
    if (needle === "" || text === "")
        return text === "" ? [] : [{ text, matched: false }];
    const haystack = text.toLowerCase();
    const lower = needle.toLowerCase();
    const segments = [];
    let cursor = 0;
    for (;;) {
        const hit = haystack.indexOf(lower, cursor);
        if (hit === -1)
            break;
        if (hit > cursor)
            segments.push({ text: text.slice(cursor, hit), matched: false });
        // Sliced out of `text`, never out of `haystack` — the marked span must carry
        // the original casing, or searching "riverbend" would rewrite the brand's
        // own name in the row it just found.
        segments.push({ text: text.slice(hit, hit + needle.length), matched: true });
        cursor = hit + needle.length;
    }
    if (cursor < text.length)
        segments.push({ text: text.slice(cursor), matched: false });
    return segments;
}
//# sourceMappingURL=match.js.map